import { authentication as msAuth } from "@microsoft/teams-js";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { env } from "../../utils/env.js";
import { updateSearchParams } from "../../utils/url-search-params.js";
import { Platform } from "../platform-detection/detect-platform.js";
import { usePlatform } from "../platform-detection/use-platform.js";
import {
  buildAdminConsentStartPath,
  buildAdminConsentStartUrl,
  rememberAdminConsentSearch,
} from "./admin-consent-logic.js";
import type { AdminConsentFlow } from "./admin-consent-logic.js";
import { useAuth } from "./use-auth.js";

interface PopupSize {
  height?: number;
  width?: number;
}

const isConsentDeniedError = (error: unknown) => {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const value = error as {
    code?: unknown;
    errorCode?: unknown;
    message?: unknown;
  };
  const text = [value.code, value.errorCode, value.message]
    .filter((part): part is string => typeof part === "string")
    .join(" ")
    .toLowerCase();

  return /cancel|canceled|cancelled|declined|denied|reject|closed|abort/u.test(
    text
  );
};

export const useAdminConsent = ({
  flow,
  popupSize,
  tenantId,
}: {
  flow: AdminConsentFlow;
  popupSize?: PopupSize;
  tenantId: string;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { platform } = usePlatform();
  const { user } = useAuth();

  return useCallback(async () => {
    rememberAdminConsentSearch(flow, location.search);

    const searchWithHint = user.email
      ? updateSearchParams(location.search, {
          set: { login_hint: user.email },
        })
      : location.search;

    const startPath = buildAdminConsentStartPath({
      flow,
      tenantId,
    });

    if (platform !== Platform.Microsoft) {
      navigate({ pathname: startPath, search: searchWithHint });
      return "redirected" as const;
    }

    try {
      await msAuth.authenticate({
        url: buildAdminConsentStartUrl({
          flow,
          frontendUrl: env.PUBLIC_FRONTEND_URL,
          search: searchWithHint,
          tenantId,
        }),
        ...popupSize,
      });
    } catch (error: unknown) {
      if (isConsentDeniedError(error)) {
        return "denied" as const;
      }

      throw error;
    }

    return "completed" as const;
  }, [
    flow,
    location.search,
    navigate,
    platform,
    popupSize,
    tenantId,
    user.email,
  ]);
};
